import { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useGetMainCategoriesQuery, useGetSecondCategoriesQuery, useGetSubCategoriesQuery, useGetSubPartCategoriesQuery } from '../../../../api';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * Technical Support Request için özel arıza kodu dropdownları
 * Backend'den gelen code değerleri ile API'deki lookupCode'ları eşleştirir
 */
const FaultCodeDropdowns = ({
    mainCategoryValue,
    secondCategoryValue,
    subCategoryValue,
    subPartCategoryValue,
    onMainCategoryChange,
    onSecondCategoryChange,
    onSubCategoryChange,
    onSubPartCategoryChange,
    disabled = false,
    showValidation = false,
    mainCategoryError = false,
    secondCategoryError = false,
    mainCategoryErrorMessage = '',
    secondCategoryErrorMessage = ''
}) => {
    const [mainCategory, setMainCategory] = useState(null);
    const [secondCategory, setSecondCategory] = useState(null);
    const [subCategory, setSubCategory] = useState(null);
    const [subPartCategory, setSubPartCategory] = useState(null);

    const [mainSearchText, setMainSearchText] = useState('');
    const [secondSearchText, setSecondSearchText] = useState('');
    const [subSearchText, setSubSearchText] = useState('');
    const [subPartSearchText, setSubPartSearchText] = useState('');

    const prevMainValueRef = useRef(null);
    const prevSecondValueRef = useRef(null);
    const prevSubValueRef = useRef(null);
    const prevSubPartValueRef = useRef(null);

    const { getCurrentLanguageId, th } = useLanguage();
    const languageId = getCurrentLanguageId();

    // API çağrıları
    const { data: mainCategoriesList = [], isLoading: isLoadingMain } = useGetMainCategoriesQuery({
        PageSize: 30,
        SearchText: mainSearchText,
        LanguageId: languageId
    });

    const { data: secondCategoriesList = [], isLoading: isLoadingSecond } = useGetSecondCategoriesQuery(
        {
            PageSize: 30,
            SearchText: secondSearchText,
            mainCategory: mainCategory?.lookupCode,
            LanguageId: languageId
        },
        { skip: !mainCategory }
    );

    const { data: subCategoriesList = [], isLoading: isLoadingSub } = useGetSubCategoriesQuery(
        {
            PageSize: 30,
            SearchText: subSearchText,
            mainCategory: mainCategory?.lookupCode,
            secondaryCategory: secondCategory?.lookupCode,
            LanguageId: languageId
        },
        { skip: !mainCategory || !secondCategory }
    );

    const { data: subPartCategoriesList = [], isLoading: isLoadingSubPart } = useGetSubPartCategoriesQuery(
        {
            PageSize: 30,
            SearchText: subPartSearchText,
            mainCategory: mainCategory?.lookupCode,
            secondaryCategory: secondCategory?.lookupCode,
            subCategory: subCategory?.lookupCode,
            LanguageId: languageId
        },
        { skip: !mainCategory || !secondCategory || !subCategory }
    );

    // Options oluştur - useMemo ile her render'da yeniden oluşturulmasını engelle
    const mainOptions = useMemo(
        () =>
            mainCategoriesList.data && mainCategoriesList.data.length > 0
                ? mainCategoriesList.data.map((cat) => ({
                      id: cat.id,
                      lookupCode: cat.lookupCode || cat.id,
                      text: cat.mainCategoryName,
                      displayValue: `${cat.lookupCode || cat.id} - ${cat.mainCategoryName}`
                  }))
                : [],
        [mainCategoriesList.data]
    );

    const secondOptions = useMemo(
        () =>
            secondCategoriesList.data && secondCategoriesList.data.length > 0
                ? secondCategoriesList.data.map((cat) => ({
                      id: cat.id,
                      lookupCode: cat.lookupCode || cat.id,
                      text: cat.secondaryCategoryName,
                      displayValue: `${cat.lookupCode || cat.id} - ${cat.secondaryCategoryName}`
                  }))
                : [],
        [secondCategoriesList.data]
    );

    const subOptions = useMemo(
        () =>
            subCategoriesList.data && subCategoriesList.data.length > 0
                ? subCategoriesList.data.map((cat) => ({
                      id: cat.id,
                      lookupCode: cat.lookupCode || cat.id,
                      text: cat.subCategoryName,
                      displayValue: `${cat.lookupCode || cat.id} - ${cat.subCategoryName}`
                  }))
                : [],
        [subCategoriesList.data]
    );

    const subPartOptions = useMemo(
        () =>
            subPartCategoriesList.data && subPartCategoriesList.data.length > 0
                ? subPartCategoriesList.data.map((cat) => ({
                      id: cat.id,
                      lookupCode: cat.lookupCode || cat.id,
                      text: cat.subPartCategoryName,
                      displayValue: `${cat.lookupCode || cat.id} - ${cat.subPartCategoryName}`
                  }))
                : [],
        [subPartCategoriesList.data]
    );

    // Value'dan lookupCode extract et
    const extractLookupCode = (value) => {
        if (!value) return null;
        if (typeof value === 'string') {
            const parts = value.split(' - ');
            return parts[0].trim();
        }
        return String(value);
    };

    // Main category value matching - lookupCode ile eşleştir
    useEffect(() => {
        if (mainCategoryValue === null || mainCategoryValue === undefined || mainOptions.length === 0 || mainCategoryValue === prevMainValueRef.current) {
            return;
        }

        const lookupCode = extractLookupCode(mainCategoryValue);
        const found = mainOptions.find((opt) => String(opt.lookupCode) === String(lookupCode));

        if (found && (!mainCategory || found.id !== mainCategory.id)) {
            setMainCategory(found);
            prevMainValueRef.current = mainCategoryValue;
        }
    }, [mainCategoryValue, mainOptions, mainCategory]);

    // Second category value matching
    useEffect(() => {
        if (secondCategoryValue === null || secondCategoryValue === undefined || secondOptions.length === 0 || !mainCategory || secondCategoryValue === prevSecondValueRef.current) {
            return;
        }

        const lookupCode = extractLookupCode(secondCategoryValue);
        const found = secondOptions.find((opt) => String(opt.lookupCode) === String(lookupCode));

        if (found && (!secondCategory || found.id !== secondCategory.id)) {
            setSecondCategory(found);
            prevSecondValueRef.current = secondCategoryValue;
        }
    }, [secondCategoryValue, secondOptions, secondCategory, mainCategory]);

    // Sub category value matching
    useEffect(() => {
        if (subCategoryValue === null || subCategoryValue === undefined || subOptions.length === 0 || !secondCategory || subCategoryValue === prevSubValueRef.current) {
            return;
        }

        const lookupCode = extractLookupCode(subCategoryValue);
        const found = subOptions.find((opt) => String(opt.lookupCode) === String(lookupCode));

        if (found && (!subCategory || found.id !== subCategory.id)) {
            setSubCategory(found);
            prevSubValueRef.current = subCategoryValue;
        }
    }, [subCategoryValue, subOptions, subCategory, secondCategory]);

    // Sub part category value matching
    useEffect(() => {
        if (subPartCategoryValue === null || subPartCategoryValue === undefined || subPartOptions.length === 0 || !subCategory || subPartCategoryValue === prevSubPartValueRef.current) {
            return;
        }

        const lookupCode = extractLookupCode(subPartCategoryValue);
        const found = subPartOptions.find((opt) => String(opt.lookupCode) === String(lookupCode));

        if (found && (!subPartCategory || found.id !== subPartCategory.id)) {
            setSubPartCategory(found);
            prevSubPartValueRef.current = subPartCategoryValue;
        }
    }, [subPartCategoryValue, subPartOptions, subPartCategory, subCategory]);

    // Change handlers
    const handleMainChange = (e) => {
        if (disabled) return;
        const newValue = e.value;
        if (newValue?.id !== mainCategory?.id) {
            setMainCategory(newValue);
            setSecondCategory(null);
            setSubCategory(null);
            setSubPartCategory(null);

            if (onMainCategoryChange) onMainCategoryChange(newValue ? newValue.displayValue : '');
            if (onSecondCategoryChange) onSecondCategoryChange('');
            if (onSubCategoryChange) onSubCategoryChange('');
            if (onSubPartCategoryChange) onSubPartCategoryChange('');
        }
    };

    const handleSecondChange = (e) => {
        if (disabled) return;
        const newValue = e.value;
        if (newValue?.id !== secondCategory?.id) {
            setSecondCategory(newValue);
            setSubCategory(null);
            setSubPartCategory(null);

            if (onSecondCategoryChange) onSecondCategoryChange(newValue ? newValue.displayValue : '');
            if (onSubCategoryChange) onSubCategoryChange('');
            if (onSubPartCategoryChange) onSubPartCategoryChange('');
        }
    };

    const handleSubChange = (e) => {
        if (disabled) return;
        const newValue = e.value;
        if (newValue?.id !== subCategory?.id) {
            setSubCategory(newValue);
            setSubPartCategory(null);

            if (onSubCategoryChange) onSubCategoryChange(newValue ? newValue.displayValue : '');
            if (onSubPartCategoryChange) onSubPartCategoryChange('');
        }
    };

    const handleSubPartChange = (e) => {
        if (disabled) return;
        const newValue = e.value;
        if (newValue?.id !== subPartCategory?.id) {
            setSubPartCategory(newValue);
            if (onSubPartCategoryChange) onSubPartCategoryChange(newValue ? newValue.displayValue : '');
        }
    };

    const dropdownStyle = { width: '100%' };
    const itemStyle = { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

    const itemTemplate = (option) => (
        <div style={itemStyle} title={option.displayValue}>
            <span style={{ fontWeight: 'bold' }}>{option.lookupCode}</span> - {option.text}
        </div>
    );

    return (
        <>
            {/* Ana Kategori */}
            <div className="col-12" style={{ flex: '0 0 20%', maxWidth: '20%' }}>
                <label className="block font-bold mb-2">{th('technicalSupport.faultCodes.mainCategory')} *</label>
                <Dropdown
                    className={showValidation && mainCategoryError ? 'p-invalid w-full' : 'w-full'}
                    style={dropdownStyle}
                    value={mainCategory}
                    options={mainOptions}
                    onChange={handleMainChange}
                    onFilter={(e) => !disabled && setMainSearchText(e.filter)}
                    optionLabel="displayValue"
                    filter={!disabled}
                    showClear={!disabled}
                    filterBy="lookupCode,text"
                    placeholder={th('preApproval.form.placeholders.mainCategory')}
                    loading={isLoadingMain}
                    disabled={disabled}
                    itemTemplate={itemTemplate}
                />
                {showValidation && mainCategoryError && <small className="p-error">{mainCategoryErrorMessage}</small>}
            </div>

            {/* Alt Kategori */}
            <div className="col-12" style={{ flex: '0 0 20%', maxWidth: '20%' }}>
                <label className="block font-bold mb-2">{th('technicalSupport.faultCodes.secondCategory')} *</label>
                <Dropdown
                    className={showValidation && secondCategoryError ? 'p-invalid w-full' : 'w-full'}
                    style={dropdownStyle}
                    value={secondCategory}
                    options={secondOptions}
                    onChange={handleSecondChange}
                    onFilter={(e) => !disabled && setSecondSearchText(e.filter)}
                    optionLabel="displayValue"
                    filter={!disabled}
                    showClear={!disabled}
                    filterBy="lookupCode,text"
                    placeholder={th('preApproval.form.placeholders.secondCategory')}
                    loading={isLoadingSecond}
                    disabled={disabled || !mainCategory}
                    itemTemplate={itemTemplate}
                />
                {showValidation && secondCategoryError && <small className="p-error">{secondCategoryErrorMessage}</small>}
            </div>

            {/* Alt Kategori */}
            <div className="col-12" style={{ flex: '0 0 20%', maxWidth: '20%' }}>
                <label className="block font-bold mb-2">
                    {th('technicalSupport.faultCodes.subCategory')}
                    <span className="ml-1">*</span>
                </label>
                <Dropdown
                    className={`w-full ${showValidation && !subCategory ? 'p-invalid' : ''}`}
                    style={dropdownStyle}
                    value={subCategory}
                    options={subOptions}
                    onChange={handleSubChange}
                    onFilter={(e) => !disabled && setSubSearchText(e.filter)}
                    optionLabel="displayValue"
                    filter={!disabled}
                    showClear={!disabled}
                    filterBy="lookupCode,text"
                    placeholder={th('preApproval.form.placeholders.subCategory')}
                    loading={isLoadingSub}
                    disabled={disabled || !secondCategory}
                    itemTemplate={itemTemplate}
                />
                {showValidation && !subCategory && <small className="p-error">{th('technicalSupport.requiredField')}</small>}
            </div>

            {/* Alt Parça Kategorisi */}
            <div className="col-12" style={{ flex: '0 0 20%', maxWidth: '20%' }}>
                <label className="block font-bold mb-2">{th('technicalSupport.faultCodes.subPartCategory')}</label>
                <Dropdown
                    className="w-full"
                    style={dropdownStyle}
                    value={subPartCategory}
                    options={subPartOptions}
                    onChange={handleSubPartChange}
                    onFilter={(e) => !disabled && setSubPartSearchText(e.filter)}
                    optionLabel="displayValue"
                    filter={!disabled}
                    showClear={!disabled}
                    filterBy="lookupCode,text"
                    placeholder={th('preApproval.form.placeholders.subPartCategory')}
                    loading={isLoadingSubPart}
                    disabled={disabled || !subCategory}
                    itemTemplate={itemTemplate}
                />
            </div>
        </>
    );
};

export default FaultCodeDropdowns;
